import FHIRPoster from 'data/FHIRPoster';
import SendGridPoster from 'data/SendGridPoster';
import type {
  BundleEntry,
  CareTeam,
  Communication,
  Flag,
  Observation,
  Patient,
  Resource,
} from 'fhir/r4';
import { CommunicationRequestBuilder, FHIRProxyClient, Referral } from 'fhir-sdoh';
import {
  EMAIL_TEMPLATES,
  REFERRAL_SURVEYS,
  REFERRAL_SUBJECT_LINES,
  TEXT_MESSAGE_TEMPLATES,
  EMAIL_SURVEYS,
  EMAIL_TEMPLATE_CONTENT,
  HAEmailsToCheckReferralType,
  HAEmailsToCheckReferralStatus,
  TEXT_MESSAGE_TEMPLATES_PINPOINT,
  COMMUNICATION_TEMPLATES,
  noShowEmail,
  CHC_ACT_REFERRAL_PARTNER_INTAKE_EMAIL_SUBJECTS,
  getReferralPartnerSummaryTemplate,
  getStateSummaryTemplate,
} from 'fhir-sdoh/common/constants';
import type { CareTeamMember } from 'fhir-sdoh/common/fhir-builders/careteam';
import * as fhirpath from 'fhirpath';
import { get } from 'lodash';
import moment from 'moment';
import { useState, useEffect } from 'react';
import {
  Image,
  Label,
  List,
  Icon,
  Modal,
  Popup,
  Card,
  Divider,
  Dimmer,
  Loader,
  Form,
  Button,
  Dropdown,
  Message,
  Checkbox,
} from 'semantic-ui-react';
import type { DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import useSWR from 'swr';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRFetcher from '../../../data/FHIRFetcher';
import PatientCDO from '../../../models/patient';
import type { PatientCDOModel } from '../../../models/patient';
import type { FlatCarePlan, FlatCareTeam } from '../../../types/flat-fhir';
import type { Fetched } from '../../../types/food';
import type { ReferralSummary } from '../../../types/patient-data';
import * as FHIRUtils from '../../../utils/FHIRUtils';
import * as Helpers from '../../../utils/Helpers';
import type { ResourceEntry } from '../../../utils/bundleNarrowing';
import { dropdownValue } from '../../../utils/dropdownValue';
import { mapKey } from '../../../utils/mapKey';
import { getProfileObservationEntries } from '../../../utils/maternityCounterUtils';
import { formatPhoneNumber } from '../../../utils/utils';
import EditPatient from '../edit';
import { ValidatePatient, type PatientFormDetails } from '../edit/utils';
import FlagsList from '../flags';
import Counter from '../profile/counter';
import type { BannerCarePlan, PatientBannerProps } from '../types';

/** Orgs that route member communications through CommunicationRequest instead of SendGrid hooks. */
const COMMUNICATION_REQUEST_ORGS = [
  'AF-ACO-MA',
  'AH-MCAID-HOUSE',
  'ANTHEM',
  'ASPI-SDOH-HC',
  'BCBSSC',
  'BCMN-FEP',
  'BCMN-MSHO-CHRO',
  'BCMN-PMAP-CHRO',
  'BCMN-PMAP-MH',
  'BCNC-COMM-MH',
  'BCNC-EMPL-DM',
  'BCNC-FEP',
  'BCNC-IAFP-DM',
  'BCNC-MCARE-DM',
  'BCSC-MCAID',
  'BCSC-MCAID-MH',
  'BCSC-MCAID-YOUTH',
  'BCSC-MCARE-PD',
  'CFBC-MCAID',
  'CHC-ACT',
  'CNT-ACA',
  'DFH-MCAID',
  'EW-HRSN-C3',
  'EW-PAAS',
  'FFS-MNT',
  'HH-MCAID',
  'HOR-MCAID-MLTSS',
  'HORIZON',
  'IONIS',
  'IUH-BC',
  'IUH-CH',
  'JP',
  'LYNX-OTC',
  'NLA-DM',
  'NLA-FOOD',
  'NLA-MH',
  'NRX',
  'OPT-MA',
  'PC',
  'PYX',
  'SCAN-MCARE-DM',
  'SCHA-MCAID',
  'TUFTS-WM-MTG',
  'UHC',
  'UHC-MCARE',
  'WCNC-ARPA-PRX',
];

/** Anchor left so the wide green-envelope panel opens toward the right and avoids left-edge clipping. */
const GREEN_ENVELOPE_POPUP_POSITION = 'bottom left';

/** Called only with `flatPatient`, which is seeded from `PatientCDO` and never nullish. */
const pullOrgEmails = (patient: Pick<PatientCDOModel, 'org'>) => {
  const org: string = patient && patient.org;
  const emails: string[] = [];
  // Certain emails are only for specific orgs
  emails.push(
    ...Object.keys(EMAIL_TEMPLATES).filter((et) => {
      const templateOrg = et.split(' ')?.[0];
      return templateOrg === org;
    }),
  );
  //for some programs, we don't want to show all program emails
  emails.push(
    ...Object.keys(EMAIL_TEMPLATES).filter((et) => {
      if (Object.keys(noShowEmail).includes(org)) {
        return et.startsWith('ALL') && !noShowEmail?.[org]!.includes(et);
      } else {
        return et.startsWith('ALL');
      }
    }),
  );
  if (org === 'ZING' || org === 'IU') {
    emails.push('NUTRITION TIPS');
    emails.push('APP DOWNLOAD EMAIL');
  }
  if (['BCMN-PMAP-CHRO', 'BCNC-EMPL-DM', 'BCNC-MCARE-DM', 'BCNC-IAFP-DM', 'NLA-DM'].includes(org)) {
    emails.push('NUTRITION TIPS');
  }
  return emails;
};

function PatientBanner(props: PatientBannerProps) {
  let emailTypes: string[] = [];
  const nutritionTypes = [
    'WEEK1 NUTRITION TIP',
    'WEEK2 NUTRITION TIP',
    'WEEK3 NUTRITION TIP',
    'WEEK4 NUTRITION TIP',
    'WEEK5 NUTRITION TIP',
    'WEEK6 NUTRITION TIP',
    'WEEK7 NUTRITION TIP',
    'WEEK8 NUTRITION TIP',
    'WEEK9 NUTRITION TIP',
    'WEEK10 NUTRITION TIP',
    'WEEK11 NUTRITION TIP',
    'WEEK12 NUTRITION TIP',
    'WEEK13 NUTRITION TIP',
    'WEEK14 NUTRITION TIP',
    'WEEK15 NUTRITION TIP',
    'WEEK16 NUTRITION TIP',
    'WEEK17 NUTRITION TIP',
    'WEEK18 NUTRITION TIP',
    'WEEK19 NUTRITION TIP',
    'WEEK20 NUTRITION TIP',
    'WEEK21 NUTRITION TIP',
    'WEEK22 NUTRITION TIP',
    'WEEK23 NUTRITION TIP',
    'WEEK24 NUTRITION TIP',
    'WEEK25 NUTRITION TIP',
    'WEEK26 NUTRITION TIP',
  ];
  // `entry` and `resource` are both optional on `Bundle`, so this is
  // `FhirResource | undefined` at runtime; `BannerCarePlan` is the name given to
  // the gap between that and what the consumers below dereference.
  const carePlan = get(props.carePlanData, 'entry[0].resource') as BannerCarePlan;
  const [flagsList, setFlagsList] = useState<{ resource: Flag }[]>([]);
  // Bare `useState()` inferred `undefined` and typed the write out of existence;
  // the only writer is the `/Flag` query's `error`.
  const [flagError, setFlagError] = useState<Error | undefined>();
  // const [mobileUser, setMobileUser] = useState(false);
  const [isUserPropsValidated, setIsUserPropsValidated] = useState(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [_openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [notification, setNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState('');
  const [operation, setOperation] = useState('');
  const [dimmerProcess, setDimmerProcess] = useState(false);
  /*
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Seeded from `PatientCDO`
   * (`PatientCDOModel`) and then replaced by `parseFHIRPatient`'s output
   * (`FlatPatient`); the two types describe the same flattened patient and neither
   * is assignable to the other — `PatientCDOModel` declares the fhirpath-derived
   * fields `string[]` and `id` `string`, `FlatPatient` has them `unknown[]` and
   * `string | undefined`. Consumers below read `flatPatient.org` as a `string` and
   * `flatPatient.email` as a mail recipient, which holds for the seed's shape but
   * not necessarily for what fhirpath returns. Same disagreement as in
   * `careplan/tasks/taskDetails`.
   */
  const [flatPatient, setFlatPatient] = useState<PatientCDOModel>(PatientCDO);
  /** `Partial` because the initial value is `{}`; the only writer is `parseFHIRCareTeam`. */
  const [careTeam, setCareTeam] = useState<Partial<FlatCareTeam>>({});
  /*
   * Written from an open-key read of `EMAIL_TEMPLATES` / `COMMUNICATION_TEMPLATES`,
   * whose index signature yields `undefined` for a template name that is not in the
   * map. Both are read for truthiness before use and posted as payload fields.
   */
  const [templateId, setTemplateId] = useState<string | undefined>('');
  const [commTemplateId, setCommTemplateId] = useState<string | undefined>('');
  const [loader, setLoader] = useState(false);
  const [subject, setSubject] = useState('');
  const [failed, setFailed] = useState('');
  const [success, setSuccess] = useState(false);
  const [tipDropdown, setTipDropdown] = useState(false);
  const [addAddressValidation, setAddAddressValidation] = useState(true);
  const [bannerColor, setBannerColor] = useState('');
  //disable send button when the email is disabled to send
  const [disabledSendButton, setDisabledSendButton] = useState(false);
  const [ccPractitioner, setCCPractitioner] = useState<string | null>(null);
  // Both are set from `fhirpath.evaluate(...)?.[0]`, whose element type this app's
  // ambient declaration states as `unknown` (see `src/fhirpath.d.ts`), and reset to
  // `null` when the care team carries no CC. Rendered directly into JSX, which
  // accepts any of those.
  const [ccPractitionerPhoneNumber, setCCPractitionerPhoneNumber] = useState<unknown>(null);
  const [ccPractitionerEmail, setCCPractitionerEmail] = useState<unknown>(null);
  // `null` until `careCoordinatorData` arrives; then the `{key, text, value}` options
  // built from it. Semantic's `Dropdown.options` does not accept `null`, so the two
  // render sites substitute `[]` for the not-yet-loaded state — which is what the
  // component already did with `null` (it maps over `options` only when truthy).
  const [ccPractitionersOption, setCCPractitionersOption] = useState<DropdownItemProps[] | null>(
    null,
  );
  /** `'<practitionerId>|<display>'`, the Dropdown option value built above. */
  const [selectedCCPractitioner, setSelectedCCPractitioner] = useState<string | null>(null); //to store update practitioner from dropdown
  const [openCC, setOpenCC] = useState(false);

  const fhirPatient: Patient = props.patient;
  const authInfo = useAuthContext();
  const options = { shouldRetryOnError: false, revalidateOnFocus: false };

  useEffect(() => {
    if (props.patient) {
      // `parseFHIRPatient` returns `FlatPatient`; this state is declared as the
      // `PatientCDOModel` it is seeded with. The two describe the same flattened
      // patient and disagree — see the note at `flatPatient`'s declaration.
      setFlatPatient(FHIRUtils.parseFHIRPatient(props.patient) as unknown as PatientCDOModel);
    }
  }, [props.patient]);

  useEffect(() => {
    if (flagsList && flagsList?.length > 0) {
      const redFlagsList = flagsList?.filter((flag) => {
        return flag?.resource?.code?.coding?.[0]?.code === 'REDFLAG';
      });
      const otherFlagsList = flagsList?.filter((flag) => {
        return (
          flag?.resource?.code?.coding?.[0]?.code === 'INFO' ||
          flag?.resource?.code?.coding?.[0]?.code === 'WARNING'
        );
      });
      if (redFlagsList?.length === 0) {
        props.setRedFlagWarning(false);
        if (otherFlagsList?.length > 0) {
          setBannerColor('red basic label');
        } else {
          setBannerColor('');
        }
      } else {
        props.setRedFlagWarning(true);
        setBannerColor('ui yellow horizontal label');
      }
    } else {
      props.setRedFlagWarning(false);
      setBannerColor('');
    }
  }, [flagsList]);

  useEffect(() => {
    authInfo.validateUSPSAddress = addAddressValidation;
    // Same two-model disagreement as at `flatPatient`'s declaration.
    validateDeliveryAddress(
      FHIRUtils.parseFHIRPatient(props.patient) as unknown as PatientCDOModel,
    );
  }, [addAddressValidation]);

  useEffect(() => {
    let flatCarePlan: FlatCarePlan | undefined;
    if (carePlan) {
      flatCarePlan = FHIRUtils.parseFHIRCarePlan(carePlan);
    }
    // SUSPECTED DEFECT — the twin of the `.find` in
    // `careplan/steps/careteam/index.tsx`. `BundleEntry.resource` is `0..1`, so an
    // entry carrying no resource throws inside the predicate and the member banner
    // fails to render. Reported, not fixed; the `!` preserves that throw rather
    // than skipping the entry, which would silently match a different care team.
    // The parameter is a `BundleEntry`, not the `{ resource?: CareTeam }` the
    // callback used to claim: `Bundle.entry` is `BundleEntry[]` and its
    // `resource` is the whole `FhirResource` union. `isResourceEntry` is NOT
    // used here — it would filter a resource-less entry out instead of throwing
    // on it, which is the behaviour the `!` above deliberately preserves.
    //
    // The `?.` on `careteam` is a separate guard and is upstream's, not ours: it
    // covers a null/undefined ELEMENT of `entry`, where the predicate evaluates
    // false and `.find` moves on. Do not collapse it into the `!` — they guard
    // two different values, and removing it turns a skipped entry into a throw.
    const careTeamResource = props?.careTeamData?.entry?.find((careteam: BundleEntry) => {
      return careteam?.resource!.id === flatCarePlan?.careTeamId;
    })?.resource; //to ensure the careteam gets dispalyed and updated are linked to the careplan

    /*
     * SUSPECTED DEFECT (pre-existing): `careTeamResource` is `undefined` whenever
     * the bundle carries no CareTeam matching this CarePlan's `careTeamId` — a
     * member whose care plan has no linked team, or a bundle that has not loaded.
     * `parseFHIRCareTeam` requires a `CareTeam` and calls `fhirpath.evaluate` on
     * it without a guard, so this throws while rendering the banner rather than
     * leaving the care-team fields blank.
     */
    // The `as` preserves the throw the block above describes rather than
    // asserting a belief that the value is present.
    setCareTeam(FHIRUtils.parseFHIRCareTeam(careTeamResource as CareTeam));
  }, [props.careTeamData]);

  useEffect(() => {
    if (props?.careCoordinatorData) {
      // `BundleEntry`, not the `{ resource: Practitioner }` this callback used
      // to claim: the parent's state holds `Bundle.entry` from a
      // `/Practitioner?identifier=CC` search, whose `resource` is `0..1` and is
      // the whole `FhirResource` union. Nothing below needs it narrowed — `id`
      // is on every resource and `fhirpath.evaluate` takes `unknown` — so the
      // claim bought nothing and hid the optionality.
      const ccPractitioners = props?.careCoordinatorData.map((practitioner: BundleEntry) => {
        const practitionerObj = practitioner.resource;
        // A practitioner with no `name` yields an empty array, so the concatenations
        // below render the literal "undefined undefined" for that entry rather than
        // dropping it. That is what happens today.
        const firstName = fhirpath.evaluate(practitionerObj, 'name.given.first()')?.[0] as
          | string
          | undefined;
        const lastName = fhirpath.evaluate(practitionerObj, 'name.family')?.[0] as
          | string
          | undefined;

        // `resource` is `0..1`, so these reads throw today on a resource-less
        // entry. The `!`s preserve that rather than dropping the practitioner
        // from the picker, which would silently shorten the list.
        return {
          key: practitionerObj!.id,
          text: firstName + ' ' + lastName,
          value: practitionerObj!.id + '|' + firstName + ' ' + lastName,
        };
      });
      setCCPractitionersOption(ccPractitioners);

      if (careTeam['CC']) {
        // `BundleEntry` for the same reason as the `.map` above. `resource` is
        // `0..1`, so the unguarded `.id` throws on a resource-less entry today;
        // the `!` states that without changing it.
        const ccPractitionerInfo = props?.careCoordinatorData?.find(
          (practitioner: BundleEntry) => practitioner.resource!.id === careTeam['CCID'],
        )?.resource;
        const email = fhirpath.evaluate(
          ccPractitionerInfo,
          "telecom.where(system='email').value",
        )?.[0];
        const phoneNumber = fhirpath.evaluate(
          ccPractitionerInfo,
          "telecom.where(system='phone').value",
        )?.[0];
        setCCPractitionerEmail(email);
        setCCPractitionerPhoneNumber(phoneNumber);
        setCCPractitioner(`${careTeam['CCID']}|${careTeam['CC']}`);
      } else {
        setCCPractitionerEmail(null);
        setCCPractitionerPhoneNumber(null);
        setCCPractitioner(null);
      }
    }
  }, [props.careCoordinatorData, careTeam]);
  //fetch org emails
  emailTypes = pullOrgEmails(flatPatient);

  // fetch flags. mutate is used for re-triggering SWR fetch. isValidating is true when the request is running
  const {
    data: flagsData,
    error: flagsError,
    mutate: refreshFlags,
  } = useSWR<Fetched<fhir4.Bundle>, Error>(
    ['/Flag/?patient=' + fhirPatient.id, authInfo.token],
    FHIRFetcher<fhir4.Bundle>,
    options,
  );

  useEffect(() => {
    const observationsData = props.profileData;
    const entries = getProfileObservationEntries(observationsData);

    const communicationData = entries?.filter((entry) => {
      /*
       * `getProfileObservationEntries` returns `unknown[]`, and lodash `get` over an
       * `unknown` root resolves to `never` — the path cannot be checked against a
       * type the function does not claim. The reads below are the two fields this
       * filter has always used.
       *
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. `entryCode?.indexOf(...) !== -1` is
       * `undefined !== -1` for an entry with no code, which is TRUE, so a code-less
       * observation passes the first conjunct rather than being excluded by it.
       */
      const entryCode = get(entry as object, 'resource.code.coding[0].code') as string | undefined;
      return (
        entryCode?.indexOf('COMMUNICATION-PREFERENCES') !== -1 &&
        get(entry as object, 'resource.valueString') === 'Do Not Contact'
      );
    });
    if (communicationData && communicationData?.length > 0) {
      props.setDoNotContact(true);
    } else {
      props.setDoNotContact(false);
    }
  }, [props.profileData, props.doNotContact]);

  useEffect(() => {
    if (flagsData && flagsData.entry && !flagsError) {
      // The key is `/Flag/?patient=…`, so every entry a search returns carries a
      // Flag; `BundleEntry.resource` alone is the whole union and optional.
      const activeAlerts = (flagsData.entry as ResourceEntry<'Flag'>[]).filter((alert) => {
        const parsedFlag = FHIRUtils.parseFHIRFlags(alert.resource);
        if (parsedFlag?.code === 'REDFLAG') {
          props.setRedFlagWarning(true);
        }
        return (
          parsedFlag.status === 'active' &&
          !parsedFlag.assignedTo &&
          (parsedFlag?.code === 'INFO' ||
            parsedFlag?.code === 'WARNING' ||
            parsedFlag?.code === 'REDFLAG')
        );
      });
      setFlagsList(activeAlerts);
    } else if (flagsData && !flagsData.entry && !flagsError) {
      setFlagsList([]);
    } else if (flagsError) {
      setFlagError(flagsError);
    }
  }, [flagsData, flagsError]);

  // TODO Remove this after testing is completed - 05122024
  // useEffect(() => {
  //   if (props.surveyData && props.surveyData.entry && !props.surveyDataError) {
  //     setMobileUser(props.surveyData.entry.length ? true : false);
  //   } else if (props.surveyData && !props.surveyData.entry && !props.surveyDataError) {
  //     setMobileUser(false);
  //   } else if (props.surveyDataError) {
  //     setMobileUser(false);
  //   }
  // }, [props.surveyData, props.surveyDataError]);

  // `async` with no `await`: dropping it changes the return value from a resolved
  // promise to `undefined`. Semantic UI ignores it, but that is still a change.
  // eslint-disable-next-line @typescript-eslint/require-await
  async function handleChange(e: React.SyntheticEvent<HTMLElement>, fieldprops: DropdownProps) {
    // A Semantic UI dropdown value is `string | number | boolean | (…)[] | undefined`,
    // and every use below is either a lookup subscript or a `string` state. Both
    // already coerced it — `mapKey` names that coercion once. Note `.toString()` on
    // the line below is NOT equivalent and is left as it is: it throws on an absent
    // value, which is the existing behaviour of the `emailType` branch.
    const selected = mapKey(fieldprops.value);
    setSubject(selected);
    let template_id: string | undefined;
    // `DropdownProps` carries an index signature, so `noPropertyAccessFromIndexSignature`
    // requires the subscript form for props semantic-ui does not declare by name.
    if (fieldprops['name'] === 'emailType') {
      if (
        fieldprops.value!.toString().includes('NUTRITION TIPS') &&
        !fieldprops.value!.toString().includes('SURVEY')
      ) {
        setTipDropdown(true);
      } else {
        setTipDropdown(false);
        // `as string` matches the read at `getEmailTemplateContent` below: the
        // template maps hold `string | Record<string, string>`, and every consumer
        // of `templateId` assumes the string arm.
        template_id = EMAIL_TEMPLATES[selected] as string | undefined;
        setTemplateId(template_id);
        setCommTemplateId(COMMUNICATION_TEMPLATES[selected]);
      }
    } else {
      template_id = EMAIL_TEMPLATES[selected] as string | undefined;
      setTemplateId(template_id);
      setCommTemplateId(COMMUNICATION_TEMPLATES[selected]);
    }
  }

  async function launchCommunicationRequest() {
    // The capture group of `/WEEK(\d+)/`, so `string` on a match; `undefined` for a
    // subject that is not a nutrition tip, or whose match has no group.
    let nutritionTipsWeekNumber: string | undefined = undefined;
    if (subject.includes(' NUTRITION TIP')) {
      const match = subject.match(/WEEK(\d+)/); //to get week number from nutrition tips, ex: "WEEK1 NUTRITION TIP" => "1"
      nutritionTipsWeekNumber = match ? match[1] : undefined;
    }
    const communicationRequest = CommunicationRequestBuilder.build(
      flatPatient.id,
      authInfo.practitionerId,
      // `undefined` for a subject `COMMUNICATION_TEMPLATES` does not carry; this
      // path runs only after a template has been picked from the dropdown, which is
      // what populates it.
      { code: commTemplateId!, display: commTemplateId! },
      moment().utc().format('YYYY-MM-DD'),
      authInfo.practitionerName,
      flatPatient.org,
      'active',
      undefined,
      nutritionTipsWeekNumber,
    );

    await FHIRPoster(`/CommunicationRequest`, authInfo.token, communicationRequest).then(
      () => {
        setLoader(false);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- a `RevalidateTrigger`; fire-and-forget is the intent and awaiting it would order this render behind a refetch.
        props.communicationsMutate();
        setTemplateId('');
        setSuccess(true);
        setFailed('');
      },
      // `{ toString(): string }` rather than `unknown`: `.toString()` on a `null` or
      // `undefined` rejection throws, and that throw is the existing behaviour.
      // `unknown` would compel a guard, which emits.
      (e: { toString(): string }) => {
        setLoader(false);
        setFailed(`Error: Failed to send web survey email(s): ${e.toString()}`);
      },
    );
  }

  async function sendEmail() {
    // `undefined > 0` is false, so an absent list reads as "no referrals" — the
    // answer the original relied on. `?? 0` states that without changing it.
    let filteredFilterData: ReferralSummary[] = [];
    if ((props.referralData?.length ?? 0) > 0) {
      filteredFilterData = props.referralData!.filter((referral: ReferralSummary) => {
        return referral.status !== 'COMPLETED';
      });
    }
    if (subject.includes('SURVEY')) {
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. Two of the pushes below take an
       * open-key read of `EMAIL_SURVEYS` / `REFERRAL_SURVEYS`, which is `undefined`
       * for a subject the table does not carry. The `!includes(...)` guard tests for
       * duplicates, not for presence, so an unlisted subject pushes `undefined` and
       * the `forEach` further down posts a survey request with no survey name.
       * Declared as what actually lands in it; a `?? ''` or a presence guard here
       * would change which requests get sent.
       */
      const filteredSurveys: (string | undefined)[] = [];
      //HA Referrals surveys
      if (flatPatient.org === 'HA') {
        // Short-circuit as a statement. The rule's only fix is an `if`, which rewrites
        // the expression rather than its type — same for the three sites below and the
        // ternary-as-statement at the end of this function.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        (props.referralData?.length ?? 0) > 0 &&
          props.referralData!.forEach((referral: ReferralSummary) => {
            if (
              subject.startsWith('ALL -') &&
              // Same value comparison as the sibling branch below: `includes` must
              // see the absent status as `undefined`, not as a coerced key.
              (HAEmailsToCheckReferralStatus[subject]?.includes(referral.status as string) ||
                HAEmailsToCheckReferralStatus[subject] === 'always')
            ) {
              const referralSurvey = EMAIL_SURVEYS[subject];
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              !filteredSurveys.includes(referralSurvey) && filteredSurveys.push(referralSurvey);
            } else if (
              (typeof REFERRAL_SURVEYS[subject] === 'string' ||
                (REFERRAL_SURVEYS[subject] != null &&
                  Object.prototype.hasOwnProperty.call(
                    REFERRAL_SURVEYS[subject],
                    mapKey(referral.type),
                  ))) &&
              // `includes` compares by value, so an absent status must stay
              // `undefined` here rather than becoming the string `'undefined'` —
              // unlike the property lookup on the line above, where the runtime
              // coercion to a key is what `mapKey` reproduces.
              (HAEmailsToCheckReferralStatus[subject]?.includes(referral.status as string) ||
                HAEmailsToCheckReferralStatus[subject] === 'always')
            ) {
              // The `?? REFERRAL_SURVEYS[subject]` fallback is what handles the
              // string arm of `TemplateMapValue`: indexing a string by a referral
              // type yields `undefined`, so the whole template falls through. That
              // is why the sub-key read stays unnarrowed here — a `typeof` guard
              // would pick a different branch for a per-type map whose entry for
              // THIS type happens to be missing.
              const surveysForSubject = REFERRAL_SURVEYS[subject] as
                | Record<string, string>
                | undefined;
              // The fallback is the whole table entry, which is the string arm of
              // `TemplateMapValue` whenever the sub-key read missed — the branch
              // condition above admits only those two shapes.
              const referralSurvey = (surveysForSubject?.[mapKey(referral.type)] ??
                REFERRAL_SURVEYS[subject]) as string | undefined;
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              !filteredSurveys.includes(referralSurvey) && filteredSurveys.push(referralSurvey);
            }
          });
      }
      //to send UHC CDC survey
      else if (flatPatient.org === 'UHC' && EMAIL_SURVEYS[subject] === 'member-cdc-uhc') {
        if (props?.referralData === undefined || filteredFilterData?.length === 0) {
          //TODO: check completed referral
          filteredSurveys.push('member-cdc-uhc');
        } else {
          props.referralData.forEach((referral: ReferralSummary) => {
            //CDC Cannot be sent if ANY Referral is still in progress
            if (referral.status !== 'IN-PROGRESS' && !filteredSurveys.includes('member-cdc-uhc')) {
              filteredSurveys.push('member-cdc-uhc');
            }
          });
        }
      }
      //any other survey email
      else {
        const survey: string = EMAIL_SURVEYS[subject] ?? '';
        filteredSurveys.push(survey);
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      filteredSurveys.length > 0
        ? // The async callback's promise is already discarded by `forEach`; awaiting it
          // would serialise sends that go out concurrently today.
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          filteredSurveys.forEach(async (survey) => {
            if (survey === '') {
              setFailed('Error: No questionnaire Id is found, unable to send web survey email(s)');
            } else {
              setLoader(true);
              const payload: Record<string, unknown> = {
                patientId: flatPatient.id,
                subject: TEXT_MESSAGE_TEMPLATES[subject] ?? REFERRAL_SUBJECT_LINES[subject],
                sender: authInfo.practitionerName,
                memberName: flatPatient.firstName,
                email: flatPatient.email,
                emailTemplate: templateId,
                emailTitle: subject,
                questionnaireId: survey,
                text: 'green envelop',
              };
              // `homePhone`/`mobilePhone` are `string[]`, so a member with two numbers on
              // file sends to "+1a,b". The cast names the coercion `+` already performs.
              if (flatPatient.homePhone) {
                payload['phoneNumber'] = (
                  '+1' + (flatPatient.homePhone as unknown as string)
                ).replaceAll('-', '');
              }
              if (flatPatient.mobilePhone) {
                payload['phoneNumber'] = (
                  '+1' + (flatPatient.mobilePhone as unknown as string)
                ).replaceAll('-', '');
              }
              payload['templateName'] = TEXT_MESSAGE_TEMPLATES_PINPOINT?.[subject];

              // TO DO: Replace the below condition with Vendor Logic Mapper
              if (flatPatient.org === 'UHC') {
                // No need to send texts for UHC program. Disabling it here as an intermim solution
                payload['phoneNumber'] = '';
              }

              if (COMMUNICATION_REQUEST_ORGS.includes(flatPatient.org)) {
                await launchCommunicationRequest();
              } else {
                await SendGridPoster(`/hooks/web-survey`, authInfo.token, payload).then(
                  () => {
                    setLoader(false);
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- a `RevalidateTrigger`; fire-and-forget is the intent and awaiting it would order this render behind a refetch.
                    props.communicationsMutate();
                    setTemplateId('');
                    setSuccess(true);
                    setFailed('');
                  },
                  (e: { toString(): string }) => {
                    setLoader(false);
                    setFailed(`Error: Failed to send web survey email(s): ${e.toString()}`);
                  },
                );
              }
            }
          })
        : setFailed('Error: No active referrals, unable to send web survey email(s)');
    } else if (
      (filteredFilterData?.length > 0 && flatPatient.org === 'HA') ||
      flatPatient.org !== 'HA'
    ) {
      //HA emails require referral data, dont send otherwise
      setLoader(true);

      const payload: Record<string, unknown> = {
        to: flatPatient.email,
        from: 'nourishedrx@nourishedrx.com',
        patientId: flatPatient.id,
        templateId: templateId,
        subject: subject,
        sender: authInfo.practitionerName,
        dynamicTemplateData: {
          memberName: flatPatient.firstName,
        },
        text: 'green envelop',
      };

      if (!COMMUNICATION_REQUEST_ORGS.includes(flatPatient.org)) {
        await SendGridPoster(`/hooks/mail`, authInfo.token, payload).then(
          () => {
            setLoader(false);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- a `RevalidateTrigger`; fire-and-forget is the intent and awaiting it would order this render behind a refetch.
            props.communicationsMutate();
            setTemplateId('');
            setSuccess(true);
            setFailed('');
          },
          () => {
            setLoader(false);
            setFailed('Error: Please contact tech team');
          },
        );
        if (TEXT_MESSAGE_TEMPLATES[subject] || REFERRAL_SUBJECT_LINES[subject]) {
          payload['message'] = TEXT_MESSAGE_TEMPLATES[subject] ?? REFERRAL_SUBJECT_LINES[subject];
          // Same array-to-string coercion as the green-envelope payload above.
          if (flatPatient.homePhone) {
            payload['phoneNumber'] = (
              '+1' + (flatPatient.homePhone as unknown as string)
            ).replaceAll('-', '');
          }
          if (flatPatient.mobilePhone) {
            payload['phoneNumber'] = (
              '+1' + (flatPatient.mobilePhone as unknown as string)
            ).replaceAll('-', '');
          }
          payload['templateName'] = TEXT_MESSAGE_TEMPLATES_PINPOINT?.[subject];
          //  // TO DO: Replace the below condition with Vendor Logic Mapper
          //  if (flatPatient.org === "UHC") {
          //    // No need to send texts for UHC program. Disabling it here as an intermim solution
          //    payload["phoneNumber"] = "";
          //  }
          await SendGridPoster(`/hooks/text`, authInfo.token, payload).then(
            () => {
              setLoader(false);
              // eslint-disable-next-line @typescript-eslint/no-floating-promises -- a `RevalidateTrigger`; fire-and-forget is the intent and awaiting it would order this render behind a refetch.
              props.communicationsMutate();
              setTemplateId('');
              setSuccess(true);
              setFailed('');
            },
            () => {
              setLoader(false);
              setFailed('Error: Please contact tech team');
            },
          );
        }
      } else {
        await launchCommunicationRequest();
      }
    } else if (
      (filteredFilterData?.length === 0 || filteredFilterData === undefined) &&
      flatPatient.org === 'HA'
    ) {
      setSuccess(false);
      setFailed('Error: No active referrals, unable to send web survey email(s)');
    }
  }
  /*
   * For Update Patient Details modal window.
   *
   * SUSPECTED DEFECT — the parameter is typed `Patient` but the modal supplies
   * `fhir4.Resource`. The only value ever passed is `FHIRUpdater`'s resolution
   * (`setFlag(false, response)` in `edit/index.tsx`), and `FHIRUpdater` resolves
   * `Resource` — the FHIR PUT response for a failed or redirected update is not
   * required to be the Patient. `parseFHIRPatient` below reads `name`, `telecom`
   * and `address` off it via fhirpath and would yield an all-empty flat patient
   * rather than throwing, which is then written into the banner's state AND
   * pushed up to `Patient360` — so a non-Patient response silently blanks the
   * member card instead of reporting a failed save.
   *
   * `Resource` is stated here because that is what the producer guarantees; the
   * `Patient` claim below is where the two sides disagree.
   */
  const toggleModal = (flag: boolean, fhirPatient?: Resource) => {
    if (operation === 'Update') {
      setOpenEdit(flag);
      setNotificationMsg('Patient details have been updated successfully...');
    } else {
      setOpenCreate(flag);
      setNotificationMsg('New patient has been added successfully...');
    }

    if (fhirPatient !== undefined && fhirPatient) {
      setNotification(true);

      // The `Patient` assertion is the disagreement described in the block above —
      // the producer guarantees only `Resource`.
      const parsedPatient = FHIRUtils.parseFHIRPatient(fhirPatient as Patient);
      // The consumers split on which flattened-patient model they name:
      // `setSelectedPatientInfo` takes `FlatPatient`, the other two `PatientCDOModel`.
      // One value, one runtime shape — the two-model disagreement recorded at
      // `flatPatient`'s declaration.
      setFlatPatient(parsedPatient as unknown as PatientCDOModel);
      props.setSelectedPatientInfo(parsedPatient); // Updating the patient of Patient360
      validateDeliveryAddress(parsedPatient as unknown as PatientCDOModel);
    }
  };

  // The parameter is unread — the body closes over `flatPatient` — but both call
  // sites pass it, and they pass the `PatientCDOModel` state, not a FHIR `Patient`.
  const contactDetails = (_flatPatient?: PatientCDOModel) => {
    // Build delivery address string from components (same format as regular address)
    const deliveryAddress =
      flatPatient.delivery_addressLine1?.[0] &&
      flatPatient.delivery_city?.[0] &&
      flatPatient.delivery_state?.[0] &&
      flatPatient.delivery_zip?.[0]
        ? `${flatPatient.delivery_addressLine1[0]} ${flatPatient.delivery_addressLine2?.[0] || ''}. ${
            flatPatient.delivery_city[0]
          }, ${flatPatient.delivery_state[0]}. ${flatPatient.delivery_zip[0]} `
        : '';

    // Check if addresses are different
    const addressesAreDifferent =
      flatPatient.address &&
      deliveryAddress &&
      flatPatient.address.trim() !== deliveryAddress.trim();
    const deliveryAddressLink = deliveryAddress || flatPatient.address;

    return (
      <List>
        <List.Item style={{ fontSize: '14px', textAlign: 'left' }}>
          <a
            href={`https://maps.google.com/?q=${deliveryAddressLink}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {deliveryAddressLink}
            {addressesAreDifferent && (
              <Popup
                content="Delivery address is different from mailing address"
                trigger={
                  <div style={{ position: 'relative', display: 'inline-block', marginLeft: '8px' }}>
                    <Icon color="purple" name="home" style={{ fontSize: '1.2rem' }} />
                    <Icon
                      name="times circle outline"
                      color="purple"
                      size="mini"
                      style={{
                        position: 'absolute',
                        bottom: '-2px',
                        right: '-2px',
                        fontSize: '0.7em',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                      }}
                    />
                  </div>
                }
              />
            )}
          </a>
        </List.Item>
        <List.Item
          style={{ fontSize: '14px', textAlign: 'left' }}
          content={
            <a
              // `email` is `string[]`; interpolating comma-joins every address on file
              // into one `to=`, which Gmail reads as a single recipient.
              href={`https://mail.google.com/mail/u/0/?view=cm&fs=1&tf=1&to=${
                flatPatient.email as unknown as string
              }&subject=New Email`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {flatPatient.email}
            </a>
          }
        />
        {/* <List.Item style={{fontSize:"14px", textAlign:"left"}} content= {formatPhoneNumber(flatPatient.mobilePhone)}/> */}
        <List.Item style={{ fontSize: '14px', textAlign: 'left' }}>
          {(flatPatient.mobilePhone[0] && flatPatient.mobilePhone[0] !== '') ||
            (flatPatient.homePhone[0] && flatPatient.homePhone[0] !== '')}
          {flatPatient.mobilePhone[0] && flatPatient.mobilePhone[0] !== '' && (
            <>{formatPhoneNumber(flatPatient.mobilePhone)}</>
          )}
          {flatPatient.homePhone[0] && flatPatient.homePhone[0] !== '' && <> &nbsp; | &nbsp; </>}
          {flatPatient.homePhone[0] && flatPatient.homePhone[0] !== '' && (
            <>{formatPhoneNumber(flatPatient.homePhone)}</>
          )}
        </List.Item>
      </List>
    );
  };

  const validateUserProps = (patient: PatientCDOModel) => {
    // `PatientFormDetails` intersects `Record<string, unknown>` for
    // `ValidatePatient`'s computed-key reads; `PatientCDOModel` is an interface and
    // has no index signature to match. A structural gap only — the value already
    // holds every field the function reads.
    const validations = ValidatePatient(
      patient as PatientFormDetails,
      'Update',
      authInfo.practitionerName,
      true,
      patient.id,
    );
    if (!validations.valid && validations.patientBannerErrors?.length > 0) {
      setValidationErrors((prevState) => [...prevState, ...validations.patientBannerErrors]);
      setIsUserPropsValidated(false);
    }
  };

  const validateDeliveryAddress = (patient: PatientCDOModel) => {
    setIsUserPropsValidated(true);
    setValidationErrors([]);
    validateUserProps(patient);

    // Validate USPS address only if the flag is true
    if (authInfo.validateUSPSAddress) {
      Helpers.validateUSPSAddress(
        patient.delivery_addressLine1?.[0],
        patient.delivery_addressLine2?.[0],
        patient.delivery_city?.[0],
        patient.delivery_state?.[0],
        patient.delivery_zip?.[0],
        patient.org,
      ).then(
        (_success) => {},
        (_errors) => {
          setIsUserPropsValidated(false);
          setValidationErrors((prevState) => [...prevState, 'Address']);
        },
      );
    }
  };

  const openPreview = () => {
    if (subject) {
      /*
       * `as string` matches how `patient/communication/summary.tsx` reads the same
       * table: `EMAIL_TEMPLATES` values are `string | Record<string, string>`, and
       * every use below assumes the string arm — the `.includes(',')` on the very
       * next line is unguarded and already throws for the record arm and for a
       * subject the table does not carry. The assertion records that assumption
       * without moving where it fails.
       */
      const email_template_ID = EMAIL_TEMPLATES?.[subject] as string;
      if (email_template_ID.includes(',')) {
        if (CHC_ACT_REFERRAL_PARTNER_INTAKE_EMAIL_SUBJECTS.has(subject)) {
          return getReferralPartnerSummaryTemplate(flatPatient.referralPartner, email_template_ID);
        }

        //CNT-ACA outreach intake surveys vary by patient state
        // `Address.state` is `0..1`; `getStateSummaryTemplate` looks it up in
        // `stateAbbreviation`, where an absent key already reads `undefined` and the
        // function returns early. The assertion keeps that path reachable.
        const patientState = props.patient?.address?.[0]?.state;
        return getStateSummaryTemplate(patientState!, email_template_ID);
      } else {
        //regular handler
        return EMAIL_TEMPLATE_CONTENT?.[email_template_ID];
      }
    } else {
      return null;
    }
  };

  const handleSelectCCPractitioner = (
    e: React.SyntheticEvent<HTMLElement>,
    { value }: DropdownProps,
  ) => {
    /*
     * The options are built above with string `value`s (`'<id>|<display>'`), which
     * `DropdownProps['value']` widens. NOT routed through `mapKey`: this value is
     * stored, then read for truthiness at the two Update buttons, so coercing an
     * absent selection to the string `'undefined'` would make it truthy and enable
     * a button the original left disabled.
     */
    setSelectedCCPractitioner(value as string | null);
  };

  const updateCareTeam = async (data: string | null, type = 'CC') => {
    const selectedPractitioner = data ? data : ccPractitioner;
    setLoader(true);
    const asignee = selectedPractitioner?.split('|')?.[0]; //selected CC practionerId
    const selectedCareTeam = {
      reference: 'Practitioner/' + selectedPractitioner?.split?.('|')[0],
      display: selectedPractitioner?.split('|')[1],
    };

    const OAMember = careTeam['OA']
      ? [
          {
            reference: 'Practitioner/' + careTeam['OAID'],
            display: careTeam['OA'],
          },
        ]
      : [];
    const RDMember = careTeam['RD']
      ? [
          {
            reference: 'Practitioner/' + careTeam['RDID'],
            display: careTeam['RD'],
          },
        ]
      : [];
    // The false branch is dead — `type` defaults to 'CC' and both call sites omit it —
    // and yields a display string, not a member list. Typed for the live branch only.
    const CCMember: CareTeamMember[] =
      type === 'CC' ? [selectedCareTeam] : (careTeam['CC'] as unknown as CareTeamMember[]);

    const OAPractitionerId = careTeam['OAID'];
    const RDPractitionerId = careTeam['RDID'];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const CCPractitionerId = selectedPractitioner?.split?.('|')[0];

    // Computed and never read — not passed to `updateCareTeam` below, no other
    // consumer. Dead upstream too (`banner/index.tsx:749` on care-betty#develop
    // @ f0be0684), so it is kept under a disable rather than renamed or removed.
    let status = 'UN-ASSIGNED';

    if (
      (OAPractitionerId !== null &&
        OAPractitionerId !== import.meta.env.VITE_DEFAULT_PRACTITIONER_WA_ID) ||
      (RDPractitionerId !== null &&
        RDPractitionerId !== import.meta.env.VITE_DEFAULT_PRACTITIONER_RD_ID)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      status = 'ASSIGNED';
    }

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    setDimmerProcess(true);
    try {
      await Referral.updateCareTeam(
        fhirPatient,
        flatPatient.org,
        // `Patient.id` is `0..1` in FHIR; this is a patient read back from the
        // server, which assigns one, and the banner only renders with it loaded.
        fhirPatient.id!,
        carePlan?.careTeam?.[0]?.reference?.split('/')?.[1] ?? careTeam?.['id'], //careTeamId
        OAMember,
        RDMember,
        CCMember,
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: authInfo.practitionerName,
        },
        authInfo.practitionerName,
        // `undefined` when neither the dropdown selection nor the current CC is set;
        // `updateCareTeam` declares it `string` and splits it unguarded. The button
        // that reaches here renders only inside the CC panel, which requires one.
        asignee!,
        'CC',
        fhirProxyClient,
      );
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- a `RevalidateTrigger`; fire-and-forget is the intent and awaiting it would order this render behind a refetch.
      props.careTeamMutate();
      setLoader(false);
      setOpenCC(false);
    } catch (error) {
      console.log('Error in updateCC():', error instanceof Error ? error.message : String(error));
      setDimmerProcess(false);
      setLoader(false);
      throw error;
    }
    setDimmerProcess(false);
  };

  const emailOptions = emailTypes?.map((x) => {
    if (flatPatient.org === 'HA') {
      let referralTypeMeetCondition = false;
      let referralStatusMeetCondition = false;
      //TODO: referral completed shows up???
      if ((props.referralData?.length ?? 0) > 0) {
        props.referralData?.forEach((referral: ReferralSummary) => {
          if (
            // `ReferralSummary.type` / `.status` are both optional; `includes`
            // compares by value, so absence stays `undefined` here.
            (HAEmailsToCheckReferralType?.[x]?.includes(referral.type as string) ||
              HAEmailsToCheckReferralType?.[x] === 'All Referrals') &&
            (HAEmailsToCheckReferralStatus?.[x]?.includes(referral.status as string) ||
              HAEmailsToCheckReferralStatus?.[x] === 'always')
          ) {
            referralTypeMeetCondition = true;
            referralStatusMeetCondition = true;
          }
        });
      }
      return {
        key: x,
        text: x.toUpperCase(),
        value: x,
        className: referralTypeMeetCondition && referralStatusMeetCondition ? '' : 'disabled item',
      };
    } else {
      return {
        key: x,
        text: x.toUpperCase(),
        value: x,
      };
    }
  });

  useEffect(() => {
    if (!subject) {
      setDisabledSendButton(false);
    } else {
      if (emailOptions) {
        const index = emailOptions?.findIndex((email) => {
          return email.key === subject;
        });
        if (emailOptions?.[index]?.className === 'disabled item') {
          setDisabledSendButton(true);
        } else {
          setDisabledSendButton(false);
        }
      }
    }
  }, [subject]);

  const getOrgDisplayName = (orgId: string) => {
    const orgDisplay = authInfo.payers.find((obj) => obj.key === orgId)?.text;
    return orgDisplay;
  };

  return (
    <>
      <Card style={{ width: '100%', float: 'left' }} className={bannerColor}>
        <Card.Content>
          {!props.redFlagWarning ? (
            <>
              <Card.Header style={{ fontSize: '22px' }}>
                <Image
                  floated="left"
                  size="mini"
                  style={{ marginRight: '5px' }}
                  src={`../images/${Helpers.getImage(flatPatient.gender === 'M', flatPatient?.firstName?.[0])}`}
                />
                {/* Both are `string[]`; a member with two given names renders "a,b". */}
                {`${flatPatient.firstName as unknown as string} ${flatPatient.lastName as unknown as string}`}
                <Popup
                  content="Edit Patient Details"
                  trigger={
                    <Icon
                      data-content="Edit patient"
                      color="blue"
                      link
                      onClick={() => {
                        setOpenEdit(true);
                        setOperation('Update');
                      }}
                      name="edit"
                      style={{ marginLeft: 5, fontSize: '1.2rem' }}
                    />
                  }
                />
                <Popup
                  on="click"
                  size="mini"
                  trigger={
                    <Icon.Group style={{ marginLeft: '5px', verticalAlign: 'bottom' }}>
                      <Icon
                        name="flag"
                        link
                        color={flagsList.length === 0 ? 'grey' : 'red'}
                        style={{ fontSize: '1.2rem' }}
                      />
                      <Icon
                        color={flagsList.length === 0 ? 'grey' : 'red'}
                        corner="top right"
                        style={{
                          fontWeight: '1000',
                          fontSize: '0.9rem',
                          top: '-8px',
                          right: '-8px',
                        }}
                      >
                        {flagsList.length}
                      </Icon>
                    </Icon.Group>
                  }
                  flowing
                >
                  <FlagsList
                    patientId={fhirPatient.id}
                    flagsList={flagsList}
                    org={flatPatient.org}
                    refreshFlags={refreshFlags}
                    flagError={flagError}
                  />
                </Popup>
                <Popup
                  wide="very"
                  on="click"
                  position={GREEN_ENVELOPE_POPUP_POSITION}
                  closeOnEscape
                  hideOnScroll
                  closeOnDocumentClick
                  onClose={() => {
                    setTemplateId('');
                    setTipDropdown(false);
                    setSuccess(false);
                    setFailed('');
                  }}
                  disabled={props.doNotContact ? true : false}
                  trigger={
                    <Icon
                      name="mail"
                      link
                      color="green"
                      style={{ marginLeft: 5, fontSize: '1.2rem' }}
                      className={props.doNotContact ? 'disabled' : ''}
                    />
                  }
                  content={
                    <>
                      <Form.Group widths="equal">
                        <Dropdown
                          basic
                          labeled
                          selection
                          search
                          direction="right"
                          placeholder="-- SELECT EMAIL TEMPLATE --"
                          autoComplete="off"
                          selectOnBlur={false}
                          name="emailType"
                          onChange={handleChange}
                          required
                          wrapSelection
                          options={emailOptions}
                        ></Dropdown>
                        &nbsp;&nbsp;
                        {tipDropdown && (
                          <Dropdown
                            basic
                            labeled
                            selection
                            direction="right"
                            placeholder="-- SELECT NUTRITION TIP --"
                            autoComplete="off"
                            selectOnBlur={false}
                            name="nutritionTipType"
                            onChange={handleChange}
                            required
                            wrapSelection
                            options={(flatPatient.org === 'BCMN-PMAP-CHRO' //we only want to show 12 nutrition tips for the 'BCMN-PMAP-CHRO'
                              ? nutritionTypes.slice(0, 12)
                              : nutritionTypes
                            ).map((x) => {
                              return {
                                key: x,
                                text: x.toUpperCase(),
                                value: x,
                              };
                            })}
                          />
                        )}
                        &nbsp;&nbsp;
                        <div>
                          {templateId && (
                            <Popup
                              trigger={
                                <Button
                                  icon="file outline icon"
                                  size="small"
                                  content="Preview"
                                  positive
                                  on="hover"
                                />
                              }
                              wide="very"
                              content={openPreview()}
                              on="hover"
                              position="bottom left"
                            />
                          )}
                          {templateId && (
                            <Button
                              icon="mail"
                              size="small"
                              content="Send"
                              positive
                              onClick={sendEmail}
                              loading={loader}
                              className={disabledSendButton ? 'ui disabled button' : ''}
                            />
                          )}
                        </div>
                        {(success || failed.length > 0) && (
                          <Message size="tiny" floating color={success ? 'green' : 'red'}>
                            {success && 'Email Sent!'}
                            {failed.length > 0 && failed}
                          </Message>
                        )}
                      </Form.Group>
                      {props.communicationsData && props.communicationsData?.entry && (
                        <List celled style={{ maxHeight: '200px', overflowY: 'scroll' }}>
                          <List.Header>
                            <b>History</b>
                          </List.Header>
                          {props.communicationsData.entry.map((cd: { resource: Communication }) => {
                            const commResource = cd.resource;
                            const practName = commResource.sender && commResource.sender.display;
                            const sentDate = moment(commResource.sent).fromNow();
                            const topicName = commResource.topic && commResource.topic.text;
                            return (
                              <List.Item>
                                <i>{topicName}</i>: Sent {sentDate} by {practName}
                              </List.Item>
                            );
                          })}
                        </List>
                      )}
                    </>
                  }
                />
                {!isUserPropsValidated && validationErrors && validationErrors.length > 0 && (
                  <Popup
                    content={
                      <>
                        {validationErrors && validationErrors.length > 0 && (
                          <h4 style={{ color: '#2185D0' }}> Missing Members Data: </h4>
                        )}
                        <List bulleted>
                          {validationErrors &&
                            validationErrors.length > 0 &&
                            validationErrors.map((error) => (
                              <List.Item key={error} as="span">
                                <List.Content>
                                  <List.Description>{error}</List.Description>
                                </List.Content>
                              </List.Item>
                            ))}
                        </List>
                      </>
                    }
                    trigger={
                      <Icon
                        data-content="Address is not valid!"
                        color="orange"
                        link
                        name="warning sign"
                        style={{ marginLeft: 5, fontSize: '1.2rem' }}
                      />
                    }
                  />
                )}
                <Popup
                  on="click"
                  size="mini"
                  trigger={
                    <Icon.Group style={{ marginLeft: '5px', verticalAlign: 'bottom' }}>
                      <Icon name="chess queen" link color="blue" style={{ fontSize: '1.2rem' }} />
                    </Icon.Group>
                  }
                  flowing
                >
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontFamily: 'lato',
                      color: '#2185D0',
                      fontSize: '1.1rem',
                      textAlign: 'left',
                      verticalAlign: 'top',
                    }}
                  >
                    Address Validation:{' '}
                  </span>
                  <Checkbox
                    style={{ marginLeft: '5px' }}
                    toggle
                    checked={addAddressValidation}
                    onChange={(_e) => setAddAddressValidation(!addAddressValidation)}
                  ></Checkbox>
                </Popup>
                <Popup
                  wide="very"
                  on="click"
                  position="top right"
                  closeOnEscape
                  hideOnScroll
                  closeOnDocumentClick
                  onClose={() => {
                    setSelectedCCPractitioner(null);
                    setOpenCC(false);
                  }}
                  onOpen={() => {
                    setOpenCC(true);
                  }}
                  open={openCC}
                  trigger={
                    <Icon.Group style={{ marginLeft: '5px', verticalAlign: 'bottom' }}>
                      <Icon
                        name="user md"
                        color="pink"
                        style={{ fontSize: '1.2rem' }}
                        disabled={!carePlan || !careTeam ? true : false}
                        onClick={() => {
                          setOpenCC(true);
                        }}
                      />
                    </Icon.Group>
                  }
                  content={
                    <>
                      <Form.Group widths="equal">
                        <Dropdown
                          basic
                          labeled
                          selection
                          search
                          placeholder="SELECT CARE COORDINATOR"
                          autoComplete="off"
                          selectOnBlur={false}
                          name="careCoordinator"
                          onChange={handleSelectCCPractitioner}
                          {...dropdownValue(
                            selectedCCPractitioner ? selectedCCPractitioner : ccPractitioner,
                          )}
                          wrapSelection
                          options={ccPractitionersOption ?? []}
                        ></Dropdown>{' '}
                        <Button
                          size="small"
                          content="Confirm"
                          positive
                          onClick={() => updateCareTeam(selectedCCPractitioner)}
                          loading={loader}
                        />
                        {(success || failed.length > 0) && (
                          <Message size="tiny" floating color={success ? 'green' : 'red'}>
                            {success && 'Care Coordinator Updated!'}
                            {failed.length > 0 && failed}
                          </Message>
                        )}
                      </Form.Group>
                    </>
                  }
                />
                {(flatPatient?.replaces || flatPatient?.replacedBy) && (
                  //show the linked profile in patient banner
                  <Popup
                    wide="very"
                    on="click"
                    position="top right"
                    closeOnEscape
                    hideOnScroll
                    closeOnDocumentClick
                    trigger={
                      <Icon.Group style={{ marginLeft: '5px', verticalAlign: 'bottom' }}>
                        <Icon name="linkify" color="grey" style={{ fontSize: '1.2rem' }} />
                      </Icon.Group>
                    }
                    content={
                      <>
                        <div>
                          <h4 className="ui header">Linked Profile Exists:</h4>

                          <a
                            href="#"
                            onClick={() =>
                              window.open(
                                `/patients/${flatPatient?.replaces || flatPatient?.replacedBy}`,
                                `${flatPatient?.replaces || flatPatient?.replacedBy}`,
                              )
                            }
                          >
                            {flatPatient?.replaces
                              ? `This member is CURRENT and replaces ${flatPatient?.replaces}`
                              : `This member is RETIRED and replaced by ${flatPatient?.replacedBy}`}
                          </a>
                        </div>
                      </>
                    }
                  />
                )}
              </Card.Header>
            </>
          ) : (
            <>
              {' '}
              <Card.Header style={{ fontSize: '22px', display: 'flex' }}>
                <Image
                  floated="left"
                  size="mini"
                  style={{ marginRight: '5px' }}
                  src={`../images/${Helpers.getImage(flatPatient.gender === 'M', flatPatient?.firstName?.[0])}`}
                />
                <Label style={{ fontSize: '18px', textAlign: 'left' }} basic>
                  {flatPatient.firstName} {flatPatient.lastName}
                  <Popup
                    content="Edit Patient Details"
                    trigger={
                      <Icon
                        data-content="Edit patient"
                        color="blue"
                        link
                        onClick={() => {
                          setOpenEdit(true);
                          setOperation('Update');
                        }}
                        name="edit"
                        style={{
                          marginLeft: '10px',
                          fontSize: '1.2rem',
                          verticalAlign: 'middle',
                        }}
                      />
                    }
                  />
                  <Popup
                    on="click"
                    size="mini"
                    trigger={
                      <Icon.Group style={{ marginLeft: -1, verticalAlign: 'middle' }}>
                        <Icon
                          name="flag"
                          link
                          color={flagsList.length === 0 ? 'grey' : 'red'}
                          style={{ fontSize: '1.2rem' }}
                          onClick={() => {
                            props.setFlagOpen(true);
                          }}
                        />
                        <Icon
                          color={flagsList.length === 0 ? 'grey' : 'red'}
                          corner="top right"
                          style={{
                            fontWeight: '1000',
                            fontSize: '0.9rem',
                            top: '-8px',
                            right: '-8px',
                          }}
                        >
                          {flagsList.length}
                        </Icon>
                      </Icon.Group>
                    }
                    flowing
                  >
                    <FlagsList
                      patientId={fhirPatient.id}
                      flagsList={flagsList}
                      org={flatPatient.org}
                      refreshFlags={refreshFlags}
                      flagError={flagError}
                      setBannerColor={setBannerColor}
                    />
                  </Popup>
                  <Popup
                    wide="very"
                    on="click"
                    position={GREEN_ENVELOPE_POPUP_POSITION}
                    closeOnEscape
                    hideOnScroll
                    closeOnDocumentClick
                    onClose={() => {
                      setTemplateId('');
                      setTipDropdown(false);
                      setSuccess(false);
                      setFailed('');
                    }}
                    trigger={
                      <Icon
                        name="mail"
                        link
                        color="green"
                        style={{
                          marginLeft: '11px',
                          marginRight: '5px',
                          fontSize: '1.2rem',
                          verticalAlign: 'middle',
                        }}
                      />
                    }
                    content={
                      <>
                        <Form.Group widths="equal">
                          <Dropdown
                            basic
                            labeled
                            selection
                            search
                            direction="right"
                            placeholder="-- SELECT EMAIL TEMPLATE --"
                            autoComplete="off"
                            selectOnBlur={false}
                            name="emailType"
                            onChange={handleChange}
                            required
                            wrapSelection
                            options={emailOptions}
                          ></Dropdown>
                          &nbsp;&nbsp;
                          {tipDropdown && (
                            <Dropdown
                              basic
                              labeled
                              selection
                              direction="right"
                              placeholder="-- SELECT NUTRITION TIP --"
                              autoComplete="off"
                              selectOnBlur={false}
                              name="nutritionTipType"
                              onChange={handleChange}
                              required
                              wrapSelection
                              options={nutritionTypes.map((x) => {
                                return {
                                  key: x,
                                  text: x.toUpperCase(),
                                  value: x,
                                };
                              })}
                            />
                          )}
                          &nbsp;&nbsp;
                          <div>
                            {templateId && (
                              <Popup
                                trigger={
                                  <Button
                                    icon="file outline icon"
                                    size="small"
                                    content="Preview"
                                    positive
                                    on="hover"
                                  />
                                }
                                wide="very"
                                content={openPreview()}
                                on="hover"
                                position="bottom left"
                              />
                            )}
                            {templateId && (
                              <Button
                                icon="mail"
                                size="small"
                                content="Send"
                                positive
                                onClick={sendEmail}
                                loading={loader}
                                className={disabledSendButton ? 'ui disabled button' : ''}
                              />
                            )}
                          </div>
                          {(success || failed.length > 0) && (
                            <Message size="tiny" floating color={success ? 'green' : 'red'}>
                              {success && 'Email Sent!'}
                              {failed.length > 0 && failed}
                            </Message>
                          )}
                        </Form.Group>
                        {props.communicationsData && props.communicationsData?.entry && (
                          <List
                            celled
                            style={{
                              maxHeight: '200px',
                              overflowY: 'scroll',
                            }}
                          >
                            <List.Header>
                              <b>History</b>
                            </List.Header>
                            {props.communicationsData.entry.map(
                              (cd: { resource: Communication }) => {
                                const commResource = cd.resource;
                                const practName =
                                  commResource.sender && commResource.sender.display;
                                const sentDate = moment(commResource.sent).fromNow();
                                const topicName = commResource.topic && commResource.topic.text;
                                return (
                                  <List.Item>
                                    <i>{topicName}</i>: Sent {sentDate} by {practName}
                                  </List.Item>
                                );
                              },
                            )}
                          </List>
                        )}
                      </>
                    }
                  />
                  {!isUserPropsValidated && (
                    <Popup
                      content={
                        <>
                          {validationErrors && validationErrors.length > 0 && (
                            <h4 style={{ color: '#2185D0' }}> Missing Members Data: </h4>
                          )}
                          <List bulleted>
                            {validationErrors &&
                              validationErrors.length > 0 &&
                              validationErrors.map((error) => (
                                <List.Item key={error} as="span">
                                  <List.Content>
                                    <List.Description>{error}</List.Description>
                                  </List.Content>
                                </List.Item>
                              ))}
                          </List>
                        </>
                      }
                      trigger={
                        <Icon
                          data-content="Address is not valid!"
                          color="orange"
                          link
                          name="warning sign"
                          style={{
                            marginLeft: '7px',
                            fontSize: '1.2rem',
                            verticalAlign: 'middle',
                          }}
                        />
                      }
                    />
                  )}
                  <Popup
                    on="click"
                    size="mini"
                    trigger={
                      <Icon.Group style={{ verticalAlign: 'bottom' }}>
                        <Icon
                          name="chess queen"
                          link
                          color="blue"
                          style={{ fontSize: '1.2rem', marginLeft: '5px' }}
                        />
                      </Icon.Group>
                    }
                    flowing
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontFamily: 'lato',
                        color: '#2185D0',
                        fontSize: '1.1rem',
                        textAlign: 'left',
                        verticalAlign: 'top',
                      }}
                    >
                      Address Validation:{' '}
                    </span>
                    <Checkbox
                      style={{ verticalAlign: 'middle' }}
                      toggle
                      checked={addAddressValidation}
                      onChange={(_e) => setAddAddressValidation(!addAddressValidation)}
                    ></Checkbox>
                  </Popup>
                  <Popup
                    wide="very"
                    on="click"
                    position="top right"
                    closeOnEscape
                    hideOnScroll
                    closeOnDocumentClick
                    onClose={() => {
                      setSelectedCCPractitioner(null);
                    }}
                    trigger={
                      <Icon.Group style={{ marginLeft: '5px', verticalAlign: 'bottom' }}>
                        <Icon
                          name="user md"
                          color="pink"
                          style={{ fontSize: '1.2rem' }}
                          disabled={!carePlan || !careTeam ? true : false}
                        />
                      </Icon.Group>
                    }
                    content={
                      <>
                        <Form.Group widths="equal">
                          <Dropdown
                            basic
                            labeled
                            selection
                            search
                            placeholder="SELECT CARE COORDINATOR"
                            autoComplete="off"
                            selectOnBlur={false}
                            name="careCoordinator"
                            onChange={handleSelectCCPractitioner}
                            {...dropdownValue(
                              selectedCCPractitioner ? selectedCCPractitioner : ccPractitioner,
                            )}
                            wrapSelection
                            options={ccPractitionersOption ?? []}
                          ></Dropdown>{' '}
                          <Button
                            size="small"
                            content="Confirm"
                            positive
                            onClick={() => updateCareTeam(selectedCCPractitioner)}
                            loading={loader}
                          />
                          {(success || failed.length > 0) && (
                            <Message size="tiny" floating color={success ? 'green' : 'red'}>
                              {success && 'Care Coordinator Updated!'}
                              {failed.length > 0 && failed}
                            </Message>
                          )}
                        </Form.Group>
                      </>
                    }
                  />
                  {(flatPatient?.replaces || flatPatient?.replacedBy) && (
                    //show the linked profile in patient banner
                    <Popup
                      wide="very"
                      on="click"
                      position="top right"
                      closeOnEscape
                      hideOnScroll
                      closeOnDocumentClick
                      trigger={
                        <Icon.Group style={{ marginLeft: '5px', verticalAlign: 'bottom' }}>
                          <Icon name="linkify" color="grey" style={{ fontSize: '1.2rem' }} />
                        </Icon.Group>
                      }
                      content={
                        <>
                          <div>
                            <h4 className="ui header">Linked Profile Exists:</h4>

                            <a
                              href="#"
                              onClick={() =>
                                window.open(
                                  `/patients/${flatPatient?.replaces || flatPatient?.replacedBy}`,
                                  `${flatPatient?.replaces || flatPatient?.replacedBy}`,
                                )
                              }
                            >
                              {flatPatient?.replaces
                                ? `This member is CURRENT and replaces ${flatPatient?.replaces}`
                                : `This member is RETIRED and replaced by ${flatPatient?.replacedBy}`}
                            </a>
                          </div>
                        </>
                      }
                    />
                  )}
                </Label>
              </Card.Header>
            </>
          )}
          <Card.Description>
            {props.redFlagWarning ? (
              <Label style={{ fontSize: '12px', textAlign: 'left', display: 'flex' }} basic>
                {contactDetails(flatPatient)}
              </Label>
            ) : (
              contactDetails(flatPatient)
            )}
          </Card.Description>
          {
            <Counter
              data={props.provenanceData}
              style={{ margin: '5px' }}
              patientId={flatPatient.id}
              org={flatPatient.org}
              sidebar={props.sidebar}
              interventioncStartDate={props.provenanceCreateDate}
              lastInterventionLaunchDate={props.lastInterventionLaunchDate}
              profileData={props.profileData}
              maternityObservations={props.maternityObservations}
              observation={props?.profileData?.entry}
              referralId={props.referralId}
              programBudget={props.programBudget}
            />
          }
          <Divider style={{ margin: '5px' }} />
          <Card.Meta style={{ width: '100%', textAlign: 'left' }}>
            <Label
              style={{ fontSize: '12px', textAlign: 'left' }}
              basic
              color="black"
            >{`${flatPatient?.genderPrefix}  ${flatPatient.age}`}</Label>
            {flatPatient?.language?.[0] && (
              <Label
                style={{ fontSize: '12px', textAlign: 'left' }}
                basic
                color="black"
              >{`${flatPatient?.language?.[0]}`}</Label>
            )}
            <Popup
              content="Organization"
              trigger={
                <Label style={{ fontSize: '12px', textAlign: 'left' }} basic color="black">
                  {getOrgDisplayName(flatPatient.org)}
                </Label>
              }
            />
            {flatPatient?.provider?.[0] && (
              <Popup
                content="Provider"
                trigger={
                  <Label style={{ fontSize: '12px', textAlign: 'left' }} basic color="black">
                    {flatPatient.provider?.[0]}
                  </Label>
                }
              />
            )}
            {flatPatient?.eligibilityGroup && (
              <Popup
                content="Eligibility Group"
                trigger={
                  <Label style={{ fontSize: '12px', textAlign: 'left' }} basic color="black">
                    {flatPatient.eligibilityGroup}
                  </Label>
                }
              />
            )}
            {flatPatient?.referralPartner && (
              <Popup
                content="Referral Partner"
                trigger={
                  <Label style={{ fontSize: '12px', textAlign: 'left' }} basic color="black">
                    {flatPatient.referralPartner}
                  </Label>
                }
              />
            )}
            <Label style={{ fontSize: '12px', textAlign: 'left' }} basic color="black">
              {flatPatient.dob}
            </Label>
            {flatPatient?.memberId?.[0] && (
              <Popup
                content="Member Id"
                trigger={
                  <Label style={{ fontSize: '12px', textAlign: 'left' }} basic color="black">
                    {flatPatient?.memberId}
                  </Label>
                }
              />
            )}
            {props.mobileUser && (
              <Popup
                content="Mobile User"
                trigger={<Icon name="mobile alternate" size="large" color="red" />}
              />
            )}
            {/*
              The predicate parameter is a `BundleEntry`, not the
              `{ resource?: Observation }` it used to claim: `Bundle.entry` is
              `BundleEntry[]` and `resource` is the whole `FhirResource` union,
              on which `valueString` does not exist. The read is left EXACTLY as
              written — every `?.` in the chain is load-bearing and erases into
              the emit — and the Observation-ness is stated on the local instead.
            */}
            {(props.profileData?.entry?.find((o: BundleEntry) =>
              (o?.resource as Observation | undefined)?.valueString
                ?.toLowerCase()
                .includes('phone only'),
            ) ||
              flagsList?.find((f) =>
                f?.resource?.code?.text?.toLowerCase().includes('phone only'),
              )) && (
              <Popup
                flowing
                position="right center"
                trigger={<Icon circular inverted color="purple" name="phone volume" size="small" />}
                content={<span>Phone Only</span>}
                on="hover"
              />
            )}
          </Card.Meta>
          <Card.Meta style={{ width: '100%', textAlign: 'left', marginTop: '4px' }}>
            <Label style={{ fontSize: '12px', textAlign: 'left' }} basic color="black">
              {flatPatient.id}
            </Label>
          </Card.Meta>

          <div
            style={{
              textAlign: 'left',
              marginTop: '4px',
              marginBottom: '-5px',
            }}
          >
            {careTeam['OA'] && (
              <Label style={{ cursor: 'default' }} basic as="a" color="black" image>
                <img src="/images/elyse.png" />
                {careTeam['OA']}
                <Label.Detail>WA</Label.Detail>
              </Label>
            )}
            {careTeam['RD'] && (
              <Label style={{ cursor: 'default' }} basic as="a" color="black" image>
                <img src="/images/nan.jpg" />
                {careTeam['RD']}
                <Label.Detail>RD</Label.Detail>
              </Label>
            )}
            {careTeam['CC'] && (
              <>
                <Label style={{ cursor: 'default' }} basic as="a" color="black" image>
                  <img src="/images/chris.jpg" />
                  {careTeam['CC']}
                  <Label.Detail>CC</Label.Detail>
                </Label>
                <Popup
                  trigger={
                    <Button
                      icon="phone"
                      size="mini"
                      // `unknown` from `fhirpath.evaluate` (see the state's
                      // declaration); the sibling `disabled` gates the click on the
                      // same value being truthy, and the telecom value is a string.
                      onClick={() =>
                        navigator.clipboard.writeText(ccPractitionerPhoneNumber as string)
                      }
                      disabled={!ccPractitionerPhoneNumber}
                    />
                  }
                  content={ccPractitionerPhoneNumber}
                  basic
                />
                <Popup
                  trigger={
                    <Button
                      icon="mail"
                      size="mini"
                      onClick={() => navigator.clipboard.writeText(ccPractitionerEmail as string)}
                      disabled={!ccPractitionerEmail}
                    />
                  }
                  content={ccPractitionerEmail}
                  basic
                />
              </>
            )}
          </div>
          <Dimmer active={dimmerProcess} inverted>
            <Loader />
          </Dimmer>
        </Card.Content>
      </Card>

      {openEdit && (
        <EditPatient
          patientId={fhirPatient.id}
          refreshPatientDetails={props.refreshPatientDetails}
          careteamStatus={careTeam['status']}
          patient={flatPatient}
          operation={'Update'}
          open={openEdit}
          userEmail={props.userEmail}
          toggleModal={toggleModal}
          organization={flatPatient.org}
          fhirPatient={fhirPatient}
        />
      )}
      {notification && (
        <Modal
          open={notification}
          size="mini"
          content={notificationMsg}
          onActionClick={() => setNotification(false)}
          actions={[{ key: 'done', icon: 'check', content: 'OK', positive: true }]}
        ></Modal>
      )}
    </>
  );
}

export default PatientBanner;
